// ============================================================================
// MarketDisputeView — ЕДИНЫЙ экран спора (оператор/участник).
//
// История и комментарии — обсуждение заявки (контекстный чат ядра). Открытие
// спора и решение — размеченные сообщения; состояние — теги заявки. Деньги:
// «возврат» вызывает существующую серверную операцию, «выплата»/«отказ» —
// фиксация решения без движения средств (см. lib/marketDispute.ts).
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import {
  CheckCircle2,
  ArrowLeft,
  Gavel,
  Loader2,
  MessageSquare,
  RefreshCw,
  ShieldAlert,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToastStore } from '@/store/toastStore';
import { useAuthStore } from '@/store/authStore';
import { eventBusHub } from '@/lib/eventBusHub';
import { friendlyError, logErrorToJournal } from '@/lib/errors';
import { formatValue } from '@/lib/format';
import { MarketStatusChip } from './MarketStatusChip';
import { MarketError, MarketLoading } from './MarketState';
import { MarketDisputeDecisionForm } from './MarketDisputeDecisionForm';
import { MarketDisputeHistory } from './MarketDisputeHistory';
import { MarketDisputeOpenForm } from './MarketDisputeOpenForm';
import {
  DISPUTE_DECISION_LABELS,
  disputeReasonLabel,
  loadMarketDispute,
  openMarketDispute,
  resolveMarketDispute,
  sendMarketDisputeComment,
  type DisputeDecision,
  type DisputeReason,
  type MarketDispute,
} from '@/lib/marketDispute';
import { isMarketRefundCandidate } from '@/lib/marketOperator';
import { useMarketOperator } from './useMarketOperator';

interface MarketDisputeViewProps {
  requestId: string;
  requestTitle: string;
  mode: 'operator' | 'participant';
  /** Разрешено ли текущему пользователю открыть спор (участник). */
  canOpen?: boolean;
  onBack?: () => void;
  onResolved?: () => void;
}

function when(value: string): string {
  return formatValue(value, 'datetime:dd.MM.yyyy HH:mm', 'timestamp');
}

function DisputeStatusChip({ status }: { status: MarketDispute['status'] }) {
  if (status === 'open') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-red-500/10 px-2.5 py-1 text-[11px] font-medium text-red-700">
        <ShieldAlert className="h-3.5 w-3.5" />
        Спор открыт
      </span>
    );
  }
  if (status === 'closed') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2.5 py-1 text-[11px] font-medium text-emerald-700">
        <CheckCircle2 className="h-3.5 w-3.5" />
        Спор решён
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-[11px] font-medium text-muted-foreground">
      Спор не открыт
    </span>
  );
}

export function MarketDisputeView({
  requestId,
  requestTitle,
  mode,
  canOpen = false,
  onBack,
  onResolved,
}: MarketDisputeViewProps) {
  const myId = String(useAuthStore((s) => s.user?.id || ''));
  const operatorAccess = useMarketOperator();
  const showSuccess = useToastStore((s) => s.showSuccess);
  const showError = useToastStore((s) => s.showError);

  const [dispute, setDispute] = useState<MarketDispute | null>(null);
  const [loading, setLoading] = useState(true);
  const [failure, setFailure] = useState('');
  const [tick, setTick] = useState(0);
  const [notice, setNotice] = useState('');

  const [opening, setOpening] = useState(false);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [resolving, setResolving] = useState(false);

  const load = useCallback(async () => {
    setFailure('');
    try {
      setDispute(await loadMarketDispute(requestId, { withRequest: true }));
    } catch (e) {
      logErrorToJournal(e, 'Спор маркетплейса: карточка');
      setFailure(friendlyError(e) || 'Не удалось загрузить спор');
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load, tick]);

  // Живое обновление: сообщения обсуждения и решения по заявке.
  const conversationId = dispute?.conversationId || '';
  useEffect(() => {
    if (!conversationId) return;
    return eventBusHub.subscribe((ev) => {
      if (
        ev.topic === `chat.message.created.${conversationId}`
        || ev.topic === `chat.message.edited.${conversationId}`
        || ev.topic.startsWith('data.document.mkt_requests.')
      ) {
        setTick((n) => n + 1);
      }
    });
  }, [conversationId]);

  const request = dispute?.request || null;
  const canRefund = !!request && isMarketRefundCandidate(request);
  const refundableHint = request && !canRefund
    ? 'Заявка не оплачена мастером (нет фактического списания за взятие) — возврата нет.'
    : '';

  const submitOpen = async (reason: DisputeReason, comment: string) => {
    if (!comment.trim()) {
      showError('Опишите ситуацию — без комментария спор не открывается');
      return;
    }
    setOpening(true);
    setNotice('');
    try {
      const result = await openMarketDispute({
        requestId,
        requestTitle,
        reason,
        comment,
        myId,
      });
      showSuccess(result.already ? 'Спор уже открыт' : 'Спор открыт — оператор увидит его в очереди');
      if (result.warnings.length > 0) setNotice(result.warnings.join(' '));
      setTick((n) => n + 1);
      onResolved?.();
    } catch (e) {
      logErrorToJournal(e, 'Спор маркетплейса: открытие');
      showError(friendlyError(e) || 'Не удалось открыть спор');
    } finally {
      setOpening(false);
    }
  };

  const submitComment = async () => {
    if (!message.trim()) return;
    setSending(true);
    try {
      const result = await sendMarketDisputeComment(requestId, message, myId, requestTitle);
      setMessage('');
      if (result.queued) {
        showSuccess('Комментарий в очереди — отправится при восстановлении связи');
      } else {
        setTick((n) => n + 1);
      }
    } catch (e) {
      logErrorToJournal(e, 'Спор маркетплейса: комментарий');
      showError(friendlyError(e) || 'Не удалось отправить комментарий');
    } finally {
      setSending(false);
    }
  };

  const submitDecision = async (decision: DisputeDecision, comment: string) => {
    setResolving(true);
    try {
      const result = await resolveMarketDispute({ requestId, decision, comment, requestTitle });
      showSuccess(
        result.alreadyResolved
          ? 'Решение уже принято — повторно не записывается'
          : result.alreadyRefunded
            ? 'Возврат уже был оформлен ранее — повторно деньги не зачислены'
            : `Решение записано: ${DISPUTE_DECISION_LABELS[decision]}`,
      );
      setTick((n) => n + 1);
      onResolved?.();
    } catch (e) {
      logErrorToJournal(e, 'Спор маркетплейса: решение');
      showError(friendlyError(e) || 'Не удалось сохранить решение');
    } finally {
      setResolving(false);
    }
  };

  if (loading && !dispute) {
    return <MarketLoading text="Загружаем спор…" />;
  }

  if (failure && !dispute) {
    return <MarketError failure={{ kind: 'other', message: failure }} onRetry={() => setTick((n) => n + 1)} />;
  }

  const openedLine = dispute?.openedAt
    ? `открыт ${when(dispute.openedAt)}${dispute.openedByName ? ` · ${dispute.openedByName}` : ''}`
    : '';
  const closedLine = dispute?.decisionAt ? `решён ${when(dispute.decisionAt)}` : '';

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        {onBack && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 rounded-lg px-2 text-xs"
            onClick={onBack}
          >
            <ArrowLeft className="mr-1 h-3.5 w-3.5" />
            К очереди
          </Button>
        )}
        <div className="min-w-0 flex-1" />
        {dispute && <DisputeStatusChip status={dispute.status} />}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 rounded-lg p-0"
          onClick={() => setTick((n) => n + 1)}
          disabled={loading}
          aria-label="Обновить"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {failure && <MarketError failure={{ kind: 'other', message: failure }} onRetry={() => setTick((n) => n + 1)} />}
      {notice && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[11px] leading-5 text-amber-800">
          {notice}
        </div>
      )}

      <section className="rounded-2xl border bg-card p-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-2">
          <div className="min-w-0 flex-1 text-sm font-semibold leading-5">{requestTitle}</div>
          {request && <MarketStatusChip status={request.status} label={request.statusLabel} />}
        </div>
        {request && (
          <>
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted-foreground">
              {request.number && <span>№ {request.number}</span>}
              {request.categoryName && <span>· {request.categoryName}</span>}
              {request.masterName && <span>· Мастер: {request.masterName}</span>}
              {mode === 'operator' && request.takePrice && <span>· Взятие: {request.takePrice}</span>}
            </div>
            {request.address && <div className="mt-1 text-xs text-muted-foreground">{request.address}</div>}
            {request.descr && (
              <p className="mt-2 line-clamp-4 whitespace-pre-wrap break-words text-xs leading-5 text-muted-foreground">
                {request.descr}
              </p>
            )}
          </>
        )}
      </section>

      {dispute && dispute.status !== 'none' && (
        <section className="rounded-2xl border bg-card p-4">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Gavel className="h-4 w-4 text-primary" />
            Предмет спора
          </div>
          <div className="mt-1 text-[11px] text-muted-foreground">
            {[dispute.openedByRole === 'master' ? 'Открыл мастер' : 'Открыл заказчик', openedLine]
              .filter(Boolean)
              .join(' · ')}
          </div>
          <div className="mt-2 text-xs font-medium">
            Причина: {dispute.reason ? disputeReasonLabel(dispute.reason) : '—'}
          </div>
          {dispute.comment && (
            <p className="mt-1 whitespace-pre-wrap break-words text-xs leading-5 text-muted-foreground">
              {dispute.comment}
            </p>
          )}
        </section>
      )}

      {dispute?.status === 'closed' && dispute.decision && (
        <section className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-emerald-800">
            <CheckCircle2 className="h-4 w-4" />
            Решение: {DISPUTE_DECISION_LABELS[dispute.decision]}
          </div>
          <div className="mt-1 text-[11px] text-muted-foreground">
            {[dispute.decisionByName, closedLine].filter(Boolean).join(' · ')}
          </div>
          {dispute.decisionComment && (
            <p className="mt-2 whitespace-pre-wrap break-words text-xs leading-5 text-muted-foreground">
              {dispute.decisionComment}
            </p>
          )}
        </section>
      )}

      {dispute?.status === 'none' && (
        <section className="rounded-2xl border bg-card p-4">
          {canOpen ? (
            <MarketDisputeOpenForm requestId={requestId} busy={opening} onOpen={(reason, comment) => void submitOpen(reason, comment)} />
          ) : (
            <div className="text-xs leading-5 text-muted-foreground">
              Спор по этой заявке не открыт. Открыть его могут заказчик и назначенный мастер.
            </div>
          )}
        </section>
      )}

      {dispute && dispute.status !== 'none' && (
        <section className="rounded-2xl border bg-card p-4">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <MessageSquare className="h-4 w-4 text-primary" />
            История и комментарии
          </div>
          <MarketDisputeHistory items={dispute.history} historyError={dispute.historyError} />

          {dispute.status === 'open' ? (
            <div className="mt-3 space-y-2">
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
                placeholder="Комментарий к спору…"
                className="w-full resize-y rounded-xl border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/40"
                disabled={sending}
              />
              <Button
                type="button"
                variant="outline"
                className="h-10 w-full rounded-xl"
                onClick={() => void submitComment()}
                disabled={sending || !message.trim()}
              >
                {sending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <MessageSquare className="mr-1.5 h-4 w-4" />}
                Отправить комментарий
              </Button>
            </div>
          ) : (
            <div className="mt-3 text-[11px] leading-5 text-muted-foreground">
              Обсуждение закрыто — история сохранена.
            </div>
          )}
        </section>
      )}

      {mode === 'operator' && operatorAccess.isOperator && dispute?.status === 'open' && (
        <section className="rounded-2xl border bg-card p-4">
          <MarketDisputeDecisionForm
            requestId={requestId}
            canRefund={canRefund}
            refundableHint={refundableHint}
            busy={resolving}
            disabledReason={dispute.historyError
              ? 'Нет доступа к обсуждению заявки — решение записать нельзя. Попросите участника добавить вас в чат заявки.'
              : ''}
            onResolve={(decision, comment) => void submitDecision(decision, comment)}
          />
        </section>
      )}

      {mode === 'participant' && dispute?.status === 'none' && !canOpen && (
        <div className="text-[11px] leading-5 text-muted-foreground">
          Если вы участник заявки и хотите открыть спор, откройте чат заявки — кнопка спора появится в обсуждении.
        </div>
      )}
    </div>
  );
}
